import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { pick, merge } from 'lodash'

// @material-ui
import { withStyles } from '@material-ui/styles'

// core
import { useGlobal } from '_core/hooks/useGlobal'
import { withLang } from '_core/hocs/withLang'
import { withCatch } from '_core/hocs/withCatch'
import {
	getProfileCoowner_mobile,
	getProfileCoowner_email,
	getProfileCoowner_firstname,
	getProfileCoowner_lastname,
	getProfileCoowner_patronymic,
	getProfileFacebook,
	getProfileVk,
	getProfileOk,
	getProfileTelegram,
	getProfileWatsapp,
	getProfileInstagram,
	getProfileYoutube,
	getProfileTiktok,
	getProfileViber,
	getProfileTwitter,
} from '_core/utils/profileProperties'
import { getFieldOptions } from '_core/utils/getFieldOptions'
import { withVisitorInfo } from '_core/utils/visitorInfo'
import { ProfileInfoFormView } from './ProfileInfoFormView'
import { State } from '_core/components/State'

const RawProfileInfoForm = (props) => {
  const { fieldsOrder, fieldsOptions, visitorInfo, getLangString: l } = props

  const {
    notify: { enqueueSnackbar },
    request: { queue },
    config: {
      API_METHOD,
      PROFILE_FIELD,
      PROFILE_FIELD_VERIFICATION_TYPE,
      VERIFICATION_STATUS,
      VISITOR_PHOTO_DEFAULT,
      // CLIENT_AGE_MAX,
      // CLIENT_AGE_MIN,
    },
  } = useGlobal()

  const fieldNames = Object.values(PROFILE_FIELD)
  const [brokenVerifTypes, setBrokenVerifTypes] = useState({})
  const [photoPath, setPhotoPath] = useState(visitorInfo.info?.photo)

  const [values, setValues] = useState({
   ...pick(visitorInfo.info, fieldNames),
   photo: 0, // visitorInfo.info?.photo?.id || 0,
   locality: visitorInfo.info?.locality,
	'properties.coowner_mobile': getProfileCoowner_mobile(visitorInfo.info),
	'properties.coowner_email': getProfileCoowner_email(visitorInfo.info),
	'properties.coowner_firstname': getProfileCoowner_firstname(visitorInfo.info),
	'properties.coowner_lastname': getProfileCoowner_lastname(visitorInfo.info),
	'properties.coowner_patronymic': getProfileCoowner_patronymic(visitorInfo.info),
	'properties.facebook': getProfileFacebook(visitorInfo.info),
	'properties.vk': getProfileVk(visitorInfo.info),
	'properties.ok': getProfileOk(visitorInfo.info),
	'properties.telegram': getProfileTelegram(visitorInfo.info),
	'properties.watsapp': getProfileWatsapp(visitorInfo.info),
	'properties.instagram': getProfileInstagram(visitorInfo.info),
	'properties.youtube': getProfileYoutube(visitorInfo.info),
	'properties.tiktok': getProfileTiktok(visitorInfo.info),
	'properties.viber': getProfileViber(visitorInfo.info),
	'properties.twitter': getProfileTwitter(visitorInfo.info),
  })

  const getVerification = async () =>
    await queue(API_METHOD.VISITOR_VERIFICATION)

  const onSubmit = (event) => {
    event.preventDefault()

    let newInfo = {
      ...values,
      birth_date: !values.birth_date ? ''
        : typeof values.birth_date === 'string' ? values.birth_date
        : values.birth_date.format('DD.MM.YYYY'),
      locality: values.locality?.id,
    }

    queue(API_METHOD.VISITOR_EDIT, newInfo)
      .then((data) => {
        const info = { ...visitorInfo.info };
        
        Object.entries(values).forEach(([key, value]) => {
          let container = info;
          
          if (key.indexOf('properties.') === 0) {
            container = info.properties;
            
            if (!container)
              info.properties = container = {};
            
            key = key.substring('properties.'.length);
          }
          
          if (typeof(value) === 'undefined')
            return;
          
          container[key] = value;
        });
        
        info.photo = photoPath;
        
        visitorInfo.set(info);

        enqueueSnackbar(l('r._.profile.info.text.success'), {
          variant: 'success',
        })
      })
      .catch((error) => null);
  }

  const getFieldProps = (options = {}, verifData = {}) => {
    const { verifiedReadOnly } = options
    const { is_required = false, state } = verifData

    if (
      verifiedReadOnly &&
      is_required &&
      state != null &&
      state !== VERIFICATION_STATUS.BLANK
    ) {
      merge(options, { viewProps: { readOnly: true } })
    }

    return options
  }

  const getFieldOnChange = (name, verifType, verifData = {}) => value => {
    const { state } = verifData
    setValues(oldValues => ({
      ...oldValues,
      [name]: value,
    }))

    if (state != null && state !== VERIFICATION_STATUS.BLANK) {
      setBrokenVerifTypes(list => {
        if (value != visitorInfo.info[name]) {
          list[verifType] = true
        } else {
          delete list[verifType]
        }

        return { ...list }
      })
    }
  }

  const getFieldOnChangeSave = () => value => {
  }

  const getEventDataFunc = getEventData => value => {
    if (Array.isArray(value) && value[0] != null) {
      setPhotoPath(value[0].path)
    }

    return getEventData(value)
  }

  const getFields = verifData => {
    return fieldsOrder.reduce((result, name) => {
      if (fieldNames.includes(name)) {
        const verifType = PROFILE_FIELD_VERIFICATION_TYPE[name];

        const { getEventData, ...options } = getFieldOptions({
          name,
          value: values[name],
          label: l(`r._.profile.info.label.${name}`),
          onChange: getFieldOnChange(name, verifType, verifData[verifType]),
          onChangeSave: getFieldOnChangeSave(
            name,
            verifType,
            verifData[verifType]
          ),
          options: getFieldProps(fieldsOptions[name], verifData[verifType]),
        });
        result[name] = options;
        result[name].getEventData =
          name === 'photo' ? getEventDataFunc(getEventData) : getEventData;
      }

      return result;
    }, {});
  };

  return (
    <State
      getProps={{ verification: getVerification }}
      render={({ verification }) => {
        return (
          <ProfileInfoFormView
            method={API_METHOD.VISITOR_EDIT}
            fields={getFields(verification.types)}
            fieldsOrder={fieldsOrder}
            fieldsOptions={fieldsOptions}
            onSubmit={onSubmit}
            userInfo={visitorInfo.info}
            brokenVerifTypes={Object.keys(brokenVerifTypes)}
            clearValue={[VISITOR_PHOTO_DEFAULT]}
            resetValue={[visitorInfo.info.photo]}
          />
        );
      }}
    />
  )
};

RawProfileInfoForm.defaultProps = {}

RawProfileInfoForm.propTypes = {
  // self props
  fieldsOptions: PropTypes.object,
  fieldsOrder: PropTypes.arrayOf(PropTypes.string),

  // withVisitorInfo
  visitorInfo: PropTypes.object.isRequired,

  // `withLang` HOC props
  langInfo: PropTypes.object,
  langStrings: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired,
}

export const ProfileInfoForm = withVisitorInfo(withCatch(
  withLang(withStyles(null, { name: 'RtsProfileInfoForm' })(RawProfileInfoForm))
))
